import React, { useState, useEffect, useCallback } from 'react';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronRight } from "react-icons/fi";
import { useAuth } from "../../utils/AuthContext";
import inmuebleService from "../../services/inmuebleService";
import espacioService from "../../services/espacioService";
import pisoService from "../../services/pisoService";
import tipoespacioService from "../../services/tipoespacioService";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from "sweetalert2";
import '../../assets/styles/form-styles.css';
import '../../assets/styles/table-styles.css';
import '../../assets/styles/EspaciosRegistrar.css';
import { BaseInput, BaseSelect, BaseButton, BaseTextarea } from '../../components/ui';

const INITIAL_FORM_STATE = {
  inmueble_id: '',
  piso_id: '',
  nombre: '',
  tipoEspacio_id: '',
  descripcion: '',
  precio: '',
  capacidad: '',
  bano: '',
  estado: '0'
};

const EspaciosRegistrar = () => {
  const { estaAutenticado } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tipoEspacios, setTipoEspacios] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [pisos, setPisos] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  // Validación del formulario
  // const validateForm = () => {
  //   const errors = [];
  //   if (!formData.inmueble_id) errors.push('Debe seleccionar un inmueble');
  //   if (!formData.piso_id) errors.push('Debe seleccionar un piso');
  //   if (!formData.nombre?.trim()) errors.push('El nombre es requerido');
  //   if (!formData.tipoEspacio) errors.push('Debe seleccionar un tipo de espacio');
  //   if (!formData.descripcion?.trim()) errors.push('La descripción es requerida');
  //   if (!formData.precio || formData.precio <= 0) errors.push('El precio debe ser mayor a 0');
  //   if (!formData.capacidad || formData.capacidad < 1) errors.push('La capacidad debe ser al menos 1');
  //   if (!formData.bano) errors.push('Debe seleccionar el tipo de baño');
  //   return errors;
  // };

  const validateField = (name, value) => {
    switch (name) {
      case "inmueble_id":
        return value ? "" : "El inmueble es requerido";
      case "piso_id":
        return value ? "" : "El piso es requerido";
      case "nombre":
        return value ? "" : "El nombre es requerido";
      case "tipoEspacio_id":
        return value ? "" : "El tipo de espacio es requerido";
      case "precio":
        return value && !isNaN(value) && Number(value) > 0
          ? ""
          : "El precio debe ser mayor a 0";
      case "capacidad":
        return value && !isNaN(value) && Number(value) >= 1
          ? ""
          : "La capacidad debe ser al menos 1";
      case "descripcion":
        return value ? "" : "La descripción es requerida";
      case "bano":
        return value ? "" : "Debe seleccionar el tipo de baño";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) errors[key] = error;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Cargar datos iniciales
  const fetchInitialData = useCallback(async () => {
    try {
      const [inmuebleData, espacioData] = await Promise.all([
        inmuebleService.obtenerInmuebles(),
        tipoespacioService.obtenerTodos()
      ]);
      setInmuebles(inmuebleData);
      setTipoEspacios(espacioData);
    } catch (error) {
      handleError(error, 'Error al cargar los datos iniciales');
    }
  }, []);

  useEffect(() => {
    if (!estaAutenticado) {
      navigate("/login");
      return;
    }
    fetchInitialData();
  }, [estaAutenticado, navigate, fetchInitialData]);

  // Manejo de errores centralizado
  const handleError = (error, message = 'Ha ocurrido un error') => {
    console.error(message, error);
    const errorMessage = error.response?.data?.message || error.message || message;
    setError(errorMessage);
    toast.error(errorMessage);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === 'inmueble_id') {
      setFormData((prev) => ({ ...prev, inmueble_id: value, piso_id: '' }));
      setFormErrors((prev) => ({
        ...prev,
        inmueble_id: validateField('inmueble_id', value),
        piso_id: '',
      }));
      if (value) {
        try {
          const pisosData = await pisoService.obtenerPorInmueble(value);
          setPisos(pisosData);
        } catch (err) {
          handleError(err, 'Error al obtener los pisos');
        }
      } else {
        setPisos([]);
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!validateForm()) {
      Swal.fire({
        title: "Campos incompletos",
        text: "Por favor corrija los errores antes de continuar",
        icon: "warning",
        confirmButtonText: "Aceptar",
      });
      setLoading(false);
      return;
    }

    try {
      const espacioData = {
        inmueble_id: formData.inmueble_id,
        piso_id: formData.piso_id,
        nombre: formData.nombre.trim(),
        tipoEspacio_id: formData.tipoEspacio_id,
        descripcion: formData.descripcion.trim(),
        precio: parseFloat(formData.precio),
        capacidad: parseInt(formData.capacidad),
        bano: formData.bano === 'propio',
        estado: 0
      };


      const espacioCreado = await espacioService.crearEspacio(espacioData);
      
      if (espacioCreado) {
        Swal.fire({
          title: "¡Éxito!",
          text: "El espacio ha sido registrado correctamente",
          icon: "success",
          confirmButtonText: "Aceptar",
        }).then(() => {
          navigate("/espacios-registros");
        });
      }
    } catch (error) {
      console.error("Error al añadir el espacio:", error);
      Swal.fire({
        title: "Error",
        text: "Error al añadir el espacio. Inténtalo de nuevo.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "Los datos no guardados se perderán",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No, continuar",
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/espacios-registros");
      }
    });
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <Sidebar id='menu-item2' id1='menu-item2' activeClassName='espacios-registrar'/>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/espacios">Espacios</Link></li>
                  <li className="breadcrumb-item"><FiChevronRight /></li>
                  <li className="breadcrumb-item active">Registrar</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="row">
                      <div className="col-12">
                        <h4 className="mb-4">Detalles del Espacio</h4>
                      </div>

                      <div className="col-12 col-md-6">
                        <BaseSelect
                          label="Inmueble"
                          name="inmueble_id"
                          required
                          isSearchable
                          value={formData.inmueble_id}
                          onChange={handleChange}
                          placeholder="Seleccione un inmueble"
                          helperText="Seleccione el inmueble donde se encuentra el espacio"
                          error={formErrors.inmueble_id}
                          options={inmuebles.map((i) => ({ value: i.id, label: i.nombre }))}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <BaseSelect
                          label="Piso"
                          name="piso_id"
                          required
                          isSearchable
                          value={formData.piso_id}
                          onChange={handleChange}
                          disabled={!formData.inmueble_id}
                          placeholder="Seleccione un piso"
                          helperText={
                            !formData.inmueble_id
                              ? 'Primero seleccione un inmueble'
                              : 'Seleccione el piso del espacio'
                          }
                          error={formErrors.piso_id}
                          options={pisos.map((p) => ({ value: p.id, label: p.nombre }))}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <BaseSelect
                          label="Tipo de Espacio"
                          name="tipoEspacio_id"
                          required
                          isSearchable
                          value={formData.tipoEspacio_id}
                          onChange={handleChange}
                          placeholder="Seleccione un tipo de espacio"
                          error={formErrors.tipoEspacio_id}
                          options={tipoEspacios.map((t) => ({ value: t.id, label: t.nombre }))}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <BaseInput
                          label="Nombre"
                          name="nombre"
                          required
                          value={formData.nombre}
                          onChange={handleChange}
                          placeholder="Ingrese el nombre del espacio"
                          maxLength={100}
                          error={formErrors.nombre}
                        />
                      </div>

                      <div className="col-12">
                        <BaseTextarea
                          label="Descripción"
                          name="descripcion"
                          required
                          rows={3}
                          value={formData.descripcion}
                          onChange={handleChange}
                          placeholder="Describa las características del espacio"
                          maxLength={500}
                          error={formErrors.descripcion}
                        />
                      </div>

                      <div className="col-md-6">
                        <BaseInput
                          label="Precio"
                          name="precio"
                          type="number"
                          required
                          restrict="decimal"
                          min="0"
                          step="0.01"
                          value={formData.precio}
                          onChange={handleChange}
                          placeholder="0.00"
                          startIcon={<span>S/</span>}
                          error={formErrors.precio}
                        />
                      </div>

                      <div className="col-md-6">
                        <BaseInput
                          label="Capacidad"
                          name="capacidad"
                          type="number"
                          required
                          restrict="number"
                          min="1"
                          value={formData.capacidad}
                          onChange={handleChange}
                          placeholder="Número de personas"
                          error={formErrors.capacidad}
                        />
                      </div>

                      <div className="col-md-6">
                        <BaseSelect
                          label="Baño"
                          name="bano"
                          required
                          value={formData.bano}
                          onChange={handleChange}
                          placeholder="Seleccione si tiene baño"
                          error={formErrors.bano}
                          options={[
                            { value: 'propio', label: 'Propio' },
                            { value: 'compartido', label: 'Compartido' },
                          ]}
                        />
                      </div>

                      <div className="col-md-6">
                        <BaseInput
                          label="Estado"
                          value="Disponible"
                          disabled
                          helperText="Los espacios nuevos siempre se crean como disponibles"
                        />
                      </div>

                      <div className="col-12 text-end mt-4 d-flex justify-content-end gap-2">
                        <BaseButton type="button" variant="secondary" onClick={handleCancel}>
                          Cancelar
                        </BaseButton>
                        <BaseButton type="submit" variant="primary" loading={loading} disabled={loading}>
                          Guardar
                        </BaseButton>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
};

export default EspaciosRegistrar;
